import type { ChannelId, NotificationPreferences, NotificationType } from "@praesto/shared-types";

/** Flat (type-only) sparse routing map — the personal mirror of Task 9's
 *  (role × type) `Rules`. Same three states as `rules-model.ts`, minus the
 *  role axis: this is the user's own `notificationPreferences.perType`,
 *  which OVERRIDES the tenant default resolved for their role. */
export type PerType = NotificationPreferences["perType"];

/** The channels configured for a `type` cell, or `undefined` if unset (falls back to the tenant-resolved default). */
export function toPerTypeChannels(
  perType: PerType | undefined,
  type: NotificationType,
): ReadonlyArray<ChannelId> | undefined {
  return perType?.[type];
}

/**
 * Classifies a `type` cell:
 *  - "default": no override configured — falls back to the tenant-resolved channels.
 *  - "off": an explicit empty override — no channels for this type.
 *  - "custom": an explicit non-empty override.
 */
export function perTypeCellState(
  perType: PerType | undefined,
  type: NotificationType,
): "default" | "custom" | "off" {
  const cell = toPerTypeChannels(perType, type);
  if (cell === undefined) return "default";
  return cell.length === 0 ? "off" : "custom";
}

/** Returns a new `PerType` map with the `type` cell set to `channels`
 *  (`undefined` removes the cell entirely, resetting it to "default"). */
export function setPerTypeCell(
  perType: PerType | undefined,
  type: NotificationType,
  channels: ReadonlyArray<ChannelId> | undefined,
): PerType {
  const next: PerType = { ...(perType ?? {}) };
  if (channels === undefined) delete next[type];
  else next[type] = channels;
  return next;
}
